import React from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Shield, CheckCircle, Zap, Database } from 'lucide-react'

export const BaresTechArchitecture: React.FC = () => {
  const { t } = useTranslation('bares')

  const features = [
    {
      icon: Zap,
      title: 'Atualização em tempo real',
      desc: 'Pedidos e contas se atualizam sozinhos para clientes e equipe — sem recarregar a tela.',
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      iconBg: 'bg-yellow-100'
    },
    {
      icon: CheckCircle,
      title: 'Dados sempre corretos',
      desc: 'A conta individual e da mesa sempre batem, evitando erros e discussões no fechamento.',
      color: 'text-amber-600',
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100'
    },
    {
      icon: Shield,
      title: 'Acesso protegido',
      desc: 'Área administrativa com permissões por perfil e registro das ações importantes.',
      color: 'text-orange-600',
      bg: 'bg-orange-50',
      iconBg: 'bg-orange-100'
    }
  ]

  return (
    <section className="py-24 bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-20 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl"></div>
      </div>

      <div className="container relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
          >
            <Shield className="w-4 h-4" />
            {t('tech.kicker', 'Tecnologia Robusta')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            Confiabilidade e Segurança
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Funcionamento estável no ritmo do salão, com informações sempre atualizadas e acesso protegido para sua equipe.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`group ${feature.bg} rounded-2xl border-2 border-gray-200 p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2`}
            >
              <div className={`w-16 h-16 ${feature.iconBg} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                <feature.icon className={`w-8 h-8 ${feature.color}`} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
              <p className="text-gray-700 leading-relaxed">{feature.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Tech Stack */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-8 shadow-lg"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
              <Database className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900">Stack Tecnológico</h3>
          </div>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Construído com as tecnologias mais modernas para garantir performance, segurança e escalabilidade.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {['React 19', 'Spring Boot 3', 'PostgreSQL', 'WebSocket', 'Redis', 'JWT + OAuth2', 'Docker', 'AWS'].map((tech) => (
              <span
                key={tech}
                className="px-4 py-2 bg-white text-gray-700 rounded-full text-sm font-medium shadow-sm border border-gray-200 hover:border-amber-300 hover:shadow-md transition-all"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
